import { z } from "zod";

import { professionalLayouts } from "./template-layouts";
export * from "./template-colors";
export * from "./template-palettes";
export * from "./template-typography";
export {
  normalizeTemplateLayout,
  preserveCustomSections,
  professionalLayouts,
} from "./template-layouts";

export const defaultLayout: string[][][] = [
  [
    ["profiles", "summary", "experience", "education", "projects", "volunteer", "references"],
    ["skills", "interests", "certifications", "awards", "publications", "languages"],
  ],
];

/** Layout par défaut adapté au nombre de colonnes du template. */
export const defaultLayoutForTemplate = (template: string): string[][][] => {
  const columns = professionalLayouts[template] ?? defaultLayout[0];
  return [columns.map((column) => [...column])];
};

// Schema
export const metadataSchema = z.object({
  template: z.string().default("project-flow"),
  layout: z.array(z.array(z.array(z.string()))).default(defaultLayout), // pages -> columns -> sections
  css: z.object({
    value: z.string().default("* {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}"),
    visible: z.boolean().default(false),
  }),
  page: z.object({
    margin: z.number().default(18),
    format: z.enum(["a4", "letter"]).default("a4"),
    options: z.object({
      breakLine: z.boolean().default(true),
      pageNumbers: z.boolean().default(true),
    }),
  }),
  theme: z.object({
    background: z.string().default("#ffffff"),
    text: z.string().default("#000000"),
    primary: z.string().default("#dc2626"),
    palette: z
      .object({
        template: z.string().max(64),
        colors: z.record(
          z.string().regex(/^(?:paper|[a-f0-9]{6})$/),
          z.string().regex(/^#[a-fA-F0-9]{6}$/),
        ),
      })
      .optional(),
  }),
  typography: z.object({
    font: z.object({
      family: z.string().default("IBM Plex Serif"),
      subset: z.string().default("latin"),
      variants: z.array(z.string()).default(["regular"]),
      size: z.number().default(14),
      custom: z.boolean().optional(),
    }),
    lineHeight: z.number().default(1.5),
    hideIcons: z.boolean().default(false),
    underlineLinks: z.boolean().default(true),
  }),
  notes: z.string().default(""),
});

// Type
export type Metadata = z.infer<typeof metadataSchema>;

// Defaults
export const defaultMetadata: Metadata = {
  template: "project-flow",
  layout: defaultLayout,
  css: {
    value: "* {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}",
    visible: false,
  },
  page: {
    margin: 18,
    format: "a4",
    options: {
      breakLine: true,
      pageNumbers: true,
    },
  },
  theme: {
    background: "#ffffff",
    text: "#000000",
    primary: "#dc2626",
  },
  typography: {
    font: {
      family: "IBM Plex Serif",
      subset: "latin",
      variants: ["regular", "italic", "600"],
      size: 14,
    },
    lineHeight: 1.5,
    hideIcons: false,
    underlineLinks: true,
  },
  notes: "",
};
